import { observer } from "mobx-react-lite";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import FontBody3 from "../../../../../components/fonts/font-body3/font-body3";
import { FONT_COLOR, FONT_VARIANT } from "../../../../../components/fonts/font-props";
import IconEdit from "../../../../../components/icons/icon-edit/icon-edit";
import { ButtonIcon } from "../../../../../components/inputs/button-icon/button-icon";
import GridLine from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line";
import GridLineButtons from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line-buttons/grid-line-buttons";
import GridLineItem from "../../../../../components/layouts/grid-line-layout/grid-line/grid-line-item/grid-line-item";
import { useNavigate } from "react-router-dom";
import IconDelete from "../../../../../components/icons/icon-delete/icon-delete";
import PolicyStore from "../../../stores/policy-store";
import FontBody4 from "../../../../../components/fonts/font-body4/font-body4";
import { processTypeOptionsList } from "../../../constants";
import IconDuplicate from "../../../../../components/icons/icon-duplicate/icon-duplicate";
import OnOffIndicator from "./components/on-off-indicator/on-off-indicator";
import "./policy-line.scss";
import IconWarning from "../../../../../components/icons/icon-warning/icon-warning";
import { ReactChild } from "react";
import { Tooltip } from "../../../../../components/tooltip/tooltip";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";

export interface IProps {
    policy: PolicyStore;
    deleteCallback: () => void;
}

const selectedAvailabilityOptions = (policy: PolicyStore): ReactChild => {
    const optionsArray = policy.getUsedForOptions().map(o => o.label);
    const selectedOptions = policy.getUsedForOptions()
            .filter(o => policy.availability.some(a => a.used_for === o.value)).map(o => o.label);

    let selectedOptionsLavel = `${selectedOptions.length} sources`;
    if (selectedOptions.length === 1) {
        selectedOptionsLavel = selectedOptions[0];
    }
    if (selectedOptions.length === optionsArray.length) {
        selectedOptionsLavel = 'All';
    }

    return (
        <Tooltip content={
            selectedOptions.map(o => <FontBody2 key={o} color={FONT_COLOR.NEUTRAL_WHITE}>{o}</FontBody2>)
        }>
            <div className="used-in-column"><FontBody4>{selectedOptionsLavel}</FontBody4></div>
        </Tooltip>
    );
};

const PolicyLine: React.FC<IProps> = observer(({ policy, deleteCallback }) => {
    const navigate = useNavigate();

    const onPolicyClicked = () => navigate({
        pathname: policy.id.toString()
    });

    const onPolicyDuplicateClicked = () => { 
        const policyId = policy.duplicate();
        navigate({
            pathname: policyId.toString()
        });
    }

    const approvalType = processTypeOptionsList().find(o => o.value === (policy.isAutoApproved ? 1 : 0))?.label;

    return <GridLine key={policy.id}
        onClick={onPolicyClicked}
        disabled={policy.isLoading()}
        settings={{
            columns: [{ width: 2 }, { width: 1 }, { width: 1 }, { width: 1 }, { width: 1 }, { width: 1 }, { width: 160, type: 'px' }]
        }}
        columns={[
            <GridLineItem>
                <FontBody1 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>
                    {policy.name}
                </FontBody1>
            </GridLineItem>,
            <GridLineItem>
                <FontBody3 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>
                    Used In
                </FontBody3>
                { selectedAvailabilityOptions(policy) }
            </GridLineItem>,
            <GridLineItem>
                <FontBody3 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>
                    Resolution
                </FontBody3>
                <div className="resolution-column">
                    {!policy.resolution && <IconWarning color="var(--color-accent-4)" />}
                    {!!policy.selectedResolutionIcon && <img src={policy.selectedResolutionIcon} alt="" width={16} height={16} />}
                    <FontBody4>{policy.resolution?.internal_name ?? 'Please Select'}</FontBody4>
                </div>
            </GridLineItem>,
            <GridLineItem>
                <FontBody3 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>
                    Return Method
                </FontBody3>
                <div className="return-method-column">
                    {!policy.returnMethod && <IconWarning color="var(--color-accent-4)" />}
                    {!!policy.selectedReturnMethodIconUrl && <img src={policy.selectedReturnMethodIconUrl} alt="" width={16} height={16} />}
                    <FontBody4>{policy.returnMethod?.name ?? 'Please Select'}</FontBody4>
                </div>
            </GridLineItem>,
            <GridLineItem>
                <FontBody3 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>
                    Fees
                </FontBody3>
                {policy.getDisplayFees().map((fee, i) => <FontBody4 key={i}>{fee}</FontBody4>)}                
            </GridLineItem>,
            <GridLineItem>
                <FontBody3 color={FONT_COLOR.PRIMARY2} variant={[FONT_VARIANT.BOLD]}>
                    Approval
                </FontBody3>
                <FontBody4>{approvalType}</FontBody4>
            </GridLineItem>,
            <GridLineButtons>
                <OnOffIndicator is_on={policy.isEnabled} />
                <ButtonIcon Component={IconDelete} onClick={deleteCallback} />
                <ButtonIcon Component={IconEdit} onClick={onPolicyClicked} />
                <ButtonIcon Component={IconDuplicate} onClick={onPolicyDuplicateClicked} />
            </GridLineButtons>,
        ]}
    />
});

export default PolicyLine;